/**
 * The parked L (M10 P5a10a, plan 2026-10-17-m10-p5a P5a10a, Q5, Q9; review F7, C1–C7): the player's L left in space
 * when its S launches is a local own-team body (`PLAYER_TEAM`, as the player's other presence ships) flown by a hold
 * law: `velocityStick` (`docking/autopilot.ts`) toward the float64 parked pose, writing `Control` like the moor
 * flight, capped at `parkedL.json holdMaxSpeedMps` (under `moor.holdSpeedMps`, so the cradle accepts it while it
 * holds). It registers itself as the cradle carrier (`DockingRuntime.setCradleCarrier`, D-1076) with the career
 * runtime's `carriedBy` writer as `onCarried`. Its pose saves as `Career.parkedL` (P5a0's key, float64, D-013) and
 * reload restores the body at that pose.
 *
 * Q5: while an L is parked in open space in this system the jump out is refused ("dock back, or moor it first");
 * moored at a ring counts as docked (spec §2.5), so leaving it there is allowed.
 *
 * P5a10b (the captain's order): `Career.parkedL.order` is `hold` or `escort`. A captained L (the worker fleet view's
 * hired captain, `parkedLCaptained`; review F23) on `escort` follows and guards the S at the `parkedL.json escort`
 * stand-off band [P 600–1,200 m] with the wing's station-keeping law, writing `Control` through the same flight
 * model as every ship, and never flies into a field clump; without a captain the order is hold only.
 *
 * Gated: nothing runs unless a `Career.parkedL` exists, and no player can own an L until the W6 flips, so today's
 * game is unchanged. `stepParkedHold` runs every tick and allocates nothing (D-014; `parkedL.test.ts` scans it).
 */
import { z } from 'zod'
import { QuatScratch, Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import parkedLJson from '../data/parkedL.json'
import type { BaySocket } from '../docking/bay'
import { ControlScratch, velocityStick, zeroControl } from '../docking/autopilot'
import type { ControlInput } from '../flight/flightModel'
import { PARKED_L_ORDERS, type Career, type ParkedL, type ParkedLOrder } from '../career/types'
import type { ColliderData } from '../assets/extract'
import { steerToward } from '../ai/steering'
import { PLAYER_TEAM, spawnShip, type Sim } from '../sim'
import type { DockingRuntime } from '../space/contracts'
import type { FleetShipView } from '../strategic/types'
import { Control, Life, Pos64, Rot, Ship, Vel } from '../world/components'

const positive = z.number().finite().positive()

/** P5a10b: the escort law's tuning (`parkedL.json escort`). */
export const parkedEscortConfigSchema = z
  .strictObject({
    /** The stand-off band from the S (m) [P 600–1,200]. */
    minM: positive,
    maxM: positive,
    /** The L corrects to `insetM` inside the band edge, so it doesn't chatter on the edge (m). */
    insetM: z.number().finite().nonnegative(),
    /** Desired closing speed per metre outside the inner band (1/s), on top of the S's velocity. */
    gain: positive,
    /** `ai/steering steerToward` gains: the nose follows the desired velocity. */
    turnGain: positive,
    rollGain: z.number().finite().nonnegative(),
    /** Below this desired speed (m/s) the nose stays (no turning on the spot). */
    faceMinSpeedMps: z.number().finite().nonnegative(),
    /** Clearance kept from a field clump's sphere (m): the L never flies into a clump. */
    keepOutMarginM: z.number().finite().nonnegative(),
    /** How far ahead (s of the desired velocity) the keep-out looks. */
    lookaheadS: positive,
  })
  .refine((e) => e.minM + 2 * e.insetM < e.maxM, { message: 'escort: minM + 2 × insetM must be under maxM' })
export type ParkedEscortConfig = z.infer<typeof parkedEscortConfigSchema>

export const parkedLConfigSchema = z.strictObject({
  /** Desired speed per metre off the parked pose (1/s): the hold law's spring. */
  holdGain: positive,
  /** The hold law's speed cap (m/s); keep it under `moor.holdSpeedMps` so a holding L stays cradle-ready. */
  holdMaxSpeedMps: positive,
  /** Q5's refusal; `{name}` is the hull's name. */
  jumpRefusal: z.string().min(1),
  /** P5a10b: the captain's escort order. */
  escort: parkedEscortConfigSchema,
})
export type ParkedLConfig = z.infer<typeof parkedLConfigSchema>

let cached: ParkedLConfig | null = null

/** Parses and validates parkedL.json (throws a ZodError naming the bad field). */
export function loadParkedLConfig(raw: unknown = parkedLJson): ParkedLConfig {
  return parkedLConfigSchema.parse(raw)
}

/** The parsed parkedL.json (cached). */
export function parkedLConfig(): ParkedLConfig {
  if (cached === null) cached = loadParkedLConfig()
  return cached
}

/** M10 P5a11b: the parked L spawned now (one at most; `spawnParkedL` sets it, `releaseParkedL` clears it), for the save path. */
let liveBody: ParkedLBody | null = null

/**
 * One parked L body: its entity, the float64 pose it holds, and pooled scratch for the hold law (D-014). The scratch
 * are classes (`Vec3Scratch`, `QuatScratch`, `ControlScratch`), not literals: the `stick` literal's tagged fields
 * boxed `velocityStick`'s three stores, 48 B per tick holding and escorting (P5a11b C5).
 */
export interface ParkedLBody {
  eid: number
  fleetId: string
  systemId: string
  /** The Career's order (saved as given); it flies `escort` only while `captained` (P5a10b; no captain = hold). */
  order: ParkedLOrder
  /** P5a10b: the L has a hired captain (the worker fleet view's `captain`, `parkedLCaptained`). */
  captained: boolean
  /** P5a10b: the last control step flew escort (leaving it re-targets the hold to where the L is). */
  escorting: boolean
  readonly target: Vec3
  readonly targetRot: Quat
  readonly desired: Vec3
  readonly rot: Quat
  readonly stick: ControlInput
}

/** What the parked L needs from the game: the sim, the cradle runtime, the cradle socket and the Career writer. */
export interface ParkedLDeps {
  readonly sim: Sim
  readonly docking: Pick<DockingRuntime, 'setCradleCarrier'>
  /** The L def's `DOCK_CRADLE` socket (`docking/cradle.ts cradleSocketOf`), or null when it has none. */
  readonly socket: BaySocket | null
  /** The L's bounding radius (m) for the cradle approach. */
  readonly radius: number
  /** The L's asset collider (absent: the sim's default for the def). */
  readonly collider?: ColliderData | null
  /** The career runtime's `Career.carriedBy` writer (D-1076's hand-off). */
  readonly onCarried: (fleetId: string | undefined) => void
}

/**
 * Spawns the parked L at its saved float64 pose (a launch, a reload or a system entry), at rest with flight assist
 * on, on the player's team, and registers it as the cradle carrier. Event-rate (allocates).
 */
export function spawnParkedL(deps: ParkedLDeps, parked: Readonly<ParkedL>, defIndex: number, captained = false): ParkedLBody {
  const [x, y, z] = parked.pos
  const [qx, qy, qz, qw] = parked.rot
  const rot = { x: qx, y: qy, z: qz, w: qw }
  const eid = spawnShip(deps.sim, defIndex, x, y, z, false, { team: PLAYER_TEAM, faOn: true, rotation: rot, collider: deps.collider })
  const body: ParkedLBody = {
    eid,
    fleetId: parked.fleetId,
    systemId: parked.systemId,
    order: parked.order,
    captained,
    escorting: false,
    target: { x, y, z },
    targetRot: rot,
    desired: new Vec3Scratch(),
    rot: new QuatScratch(qx, qy, qz, qw),
    stick: new ControlScratch(),
  }
  if (deps.socket !== null) {
    deps.docking.setCradleCarrier?.({ eid, fleetId: parked.fleetId, socket: deps.socket, radius: deps.radius, onCarried: deps.onCarried })
  }
  liveBody = body
  return body
}

/** Unregisters the carrier (the L leaves the scene: boarded, a system switch). The body itself is the caller's. */
export function releaseParkedL(deps: Pick<ParkedLDeps, 'docking'>): void {
  liveBody = null
  deps.docking.setCradleCarrier?.(null)
}

/**
 * The hold law, once per control tick: the L flies back toward its parked pose at `holdGain` per metre off, capped
 * at `holdMaxSpeedMps`, flight assist on, no turn (flight assist holds the attitude). False when the body is dead.
 * The cradle's own hold order (`docking.ts holdCarrier`) runs later in the tick and wins while a request is live.
 * Allocation-free (D-014).
 */
export function stepParkedHold(body: ParkedLBody, sim: Sim, cfg: Readonly<ParkedLConfig>): boolean {
  const e = body.eid
  if (e < 0 || Life.alive[e] !== 1) return false
  const t = body.target
  const v = body.desired
  v.x = (t.x - Pos64.x[e]) * cfg.holdGain
  v.y = (t.y - Pos64.y[e]) * cfg.holdGain
  v.z = (t.z - Pos64.z[e]) * cfg.holdGain
  const sp = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
  if (sp > cfg.holdMaxSpeedMps) {
    const k = cfg.holdMaxSpeedMps / sp
    v.x *= k
    v.y *= k
    v.z *= k
  }
  const r = body.rot
  r.x = Rot.x[e]
  r.y = Rot.y[e]
  r.z = Rot.z[e]
  r.w = Rot.w[e]
  const s = velocityStick(r, v, sim.defs[Ship.defIndex[e]].maxSpeed, zeroControl(body.stick))
  Control.surge[e] = s.surge
  Control.sway[e] = s.sway
  Control.heave[e] = s.heave
  Control.pitch[e] = 0
  Control.yaw[e] = 0
  Control.roll[e] = 0
  Control.boost[e] = 0
  Control.firePrimary[e] = 0
  Control.fireSecondary[e] = 0
  Ship.faOn[e] = 1
  return true
}

/** P5a10b: field clumps the escort never flies into: packed spheres `x, y, z, r` (float64 world metres, D-013). */
export interface ParkedKeepOut {
  readonly spheres: Float64Array
  /** Spheres in use (the first `count × 4` numbers). */
  count: number
}

/**
 * P5a10b, the escort law, once per control tick: the wing's station-keeping law (`wing/commands.ts keepStation`:
 * the leader's velocity plus a gain × the error, capped at `maxSpeed`, flight assist on, through `velocityStick`
 * like the hold) aimed at a stand-off point on the line from the S to the L, `escort.minM + insetM` to
 * `escort.maxM − insetM` from the S; inside that inner band the L just matches the S's velocity. The nose turns to
 * the desired velocity (`ai/steering steerToward`, as the AI pilot turns). Keep-out: when the look-ahead segment
 * (`lookaheadS` of the desired velocity) crosses a clump's sphere plus `keepOutMarginM`, the velocity into that
 * sphere is dropped (and the L is pushed out if it is already inside), so it waits at the edge rather than follow the S into a field clump. Never boosts or
 * fires (its turrets act on their own as today). False when the L or the S is dead. Allocation-free (D-014).
 */
export function stepParkedEscort(body: ParkedLBody, sim: Sim, cfg: Readonly<ParkedLConfig>, leader: number, keepOut: Readonly<ParkedKeepOut> | null): boolean {
  const e = body.eid
  if (e < 0 || Life.alive[e] !== 1 || leader < 0 || Life.alive[leader] !== 1) return false
  const ec = cfg.escort
  const dx = Pos64.x[e] - Pos64.x[leader]
  const dy = Pos64.y[e] - Pos64.y[leader]
  const dz = Pos64.z[e] - Pos64.z[leader]
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const lo = ec.minM + ec.insetM
  const hi = ec.maxM - ec.insetM
  const want = d < lo ? lo : d > hi ? hi : d
  // The error toward the stand-off point, along the S → L line (on top of the S: straight out along +x).
  const k = d > 1e-6 ? (ec.gain * (want - d)) / d : 0
  const v = body.desired
  v.x = Vel.x[leader] + (d > 1e-6 ? k * dx : ec.gain * lo)
  v.y = Vel.y[leader] + k * dy
  v.z = Vel.z[leader] + k * dz
  if (keepOut !== null) keepOutOfClumps(e, v, ec, keepOut)
  const max = sim.defs[Ship.defIndex[e]].maxSpeed
  const sp = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
  if (sp > max) {
    const s = max / sp
    v.x *= s
    v.y *= s
    v.z *= s
  }
  const r = body.rot
  r.x = Rot.x[e]
  r.y = Rot.y[e]
  r.z = Rot.z[e]
  r.w = Rot.w[e]
  const s = velocityStick(r, v, max, zeroControl(body.stick))
  Control.surge[e] = s.surge
  Control.sway[e] = s.sway
  Control.heave[e] = s.heave
  if (sp > ec.faceMinSpeedMps) {
    steerToward(e, v.x, v.y, v.z, ec.turnGain, ec.rollGain)
  } else {
    Control.pitch[e] = 0
    Control.yaw[e] = 0
    Control.roll[e] = 0
  }
  Control.boost[e] = 0
  Control.firePrimary[e] = 0
  Control.fireSecondary[e] = 0
  Ship.faOn[e] = 1
  return true
}

/** Drops the velocity into any clump sphere the look-ahead segment crosses (pushes out from inside). D-014. */
function keepOutOfClumps(e: number, v: Vec3, ec: Readonly<ParkedEscortConfig>, keepOut: Readonly<ParkedKeepOut>): void {
  const sph = keepOut.spheres
  const px = Pos64.x[e]
  const py = Pos64.y[e]
  const pz = Pos64.z[e]
  for (let i = 0; i < keepOut.count; i++) {
    const cx = sph[i * 4]
    const cy = sph[i * 4 + 1]
    const cz = sph[i * 4 + 2]
    const rr = sph[i * 4 + 3] + ec.keepOutMarginM
    const ox = px - cx
    const oy = py - cy
    const oz = pz - cz
    // The closest point to the centre on the look-ahead segment (a lone end point can jump over the sphere).
    const vv = v.x * v.x + v.y * v.y + v.z * v.z
    const tc = vv > 1e-9 ? -(ox * v.x + oy * v.y + oz * v.z) / vv : 0
    const ts = tc < 0 ? 0 : tc > ec.lookaheadS ? ec.lookaheadS : tc
    const ax = ox + v.x * ts
    const ay = oy + v.y * ts
    const az = oz + v.z * ts
    if (ax * ax + ay * ay + az * az >= rr * rr) continue
    const od = Math.sqrt(ox * ox + oy * oy + oz * oz)
    if (od < 1e-6) continue
    const nx = ox / od
    const ny = oy / od
    const nz = oz / od
    const vn = v.x * nx + v.y * ny + v.z * nz
    if (vn < 0) {
      v.x -= vn * nx
      v.y -= vn * ny
      v.z -= vn * nz
    }
    if (od < rr) {
      const out = ec.gain * (rr - od)
      v.x += out * nx
      v.y += out * ny
      v.z += out * nz
    }
  }
}

/**
 * P5a10b, the parked L's control step: `escort` while the order is escort, the L has a captain and the S is alive;
 * otherwise the hold law. Leaving escort (the order changed, the captain left, the S was carried or lost) holds
 * where the L is then, not back at the old parked pose. Allocation-free (D-014).
 */
export function stepParkedL(body: ParkedLBody, sim: Sim, cfg: Readonly<ParkedLConfig>, leader: number, keepOut: Readonly<ParkedKeepOut> | null): boolean {
  const e = body.eid
  if (e < 0 || Life.alive[e] !== 1) return false
  if (body.order === 'escort' && body.captained && stepParkedEscort(body, sim, cfg, leader, keepOut)) {
    body.escorting = true
    return true
  }
  if (body.escorting) {
    body.escorting = false
    body.target.x = Pos64.x[e]
    body.target.y = Pos64.y[e]
    body.target.z = Pos64.z[e]
    body.targetRot.x = Rot.x[e]
    body.targetRot.y = Rot.y[e]
    body.targetRot.z = Rot.z[e]
    body.targetRot.w = Rot.w[e]
  }
  return stepParkedHold(body, sim, cfg)
}

/**
 * P5a10b, the captain read (review F23): true when the worker fleet view (`FleetShipView.captain`, built from
 * `PlayerShips.captain`) gives the L a hired captain. A striking or recovering captain gives no orders, so the L
 * holds. Reads the view only; worker state is never changed here. Event-rate.
 */
export function parkedLCaptained(view: { readonly ships: readonly Pick<FleetShipView, 'id' | 'captain'>[] }, fleetId: string): boolean {
  const ship = view.ships.find((s) => s.id === fleetId)
  return ship?.captain?.state === 'hired'
}

/** The order the L flies: escort needs a captain; without one it holds. */
export function effectiveParkedOrder(order: ParkedLOrder, captained: boolean): ParkedLOrder {
  return captained ? order : 'hold'
}

/** The orders the fleet panel offers for the parked L: both with a captain, hold only without. */
export function parkedOrderChoices(captained: boolean): readonly ParkedLOrder[] {
  return captained ? PARKED_L_ORDERS : HOLD_ONLY
}
const HOLD_ONLY: readonly ParkedLOrder[] = ['hold']

/** The fleet panel's order change on `Career.parkedL.order` (immutable). Escort without a captain is refused. */
export function withParkedOrder(
  career: Readonly<Career>,
  order: ParkedLOrder,
  captained: boolean,
): { ok: true; career: Career } | { ok: false; reason: 'noParkedL' | 'noCaptain' } {
  const parked = career.parkedL
  if (parked === undefined) return { ok: false, reason: 'noParkedL' }
  if (order === 'escort' && !captained) return { ok: false, reason: 'noCaptain' }
  return { ok: true, career: { ...career, parkedL: { ...parked, order } } }
}

/** Event-rate: the live body takes a new order or captain read (the next `stepParkedL` acts on it). */
export function setParkedOrder(body: ParkedLBody, order: ParkedLOrder, captained: boolean): void {
  body.order = order
  body.captained = captained
}

/** The body's live pose as the `Career.parkedL` to save (float64 numbers straight from `Pos64`). Event-rate. */
export function parkedLRecord(body: Readonly<ParkedLBody>): ParkedL {
  const e = body.eid
  return {
    fleetId: body.fleetId,
    systemId: body.systemId,
    pos: [Pos64.x[e], Pos64.y[e], Pos64.z[e]],
    rot: [Rot.x[e], Rot.y[e], Rot.z[e], Rot.w[e]],
    order: body.order,
  }
}

/**
 * Q5: the refusal text when the player would jump out of `systemId` while their L is parked in open space there, or
 * null when the jump may go (nothing parked, parked elsewhere, or moored at a ring: moored = docked).
 */
export function parkedJumpRefusal(
  parked: Readonly<ParkedL> | undefined,
  systemId: string,
  moored: boolean,
  name: string,
  cfg: Readonly<ParkedLConfig> = parkedLConfig(),
): string | null {
  if (parked === undefined || parked.systemId !== systemId || moored) return null
  return cfg.jumpRefusal.replace('{name}', name)
}

// ---------------------------------------------------------------------------------------------------------------
// M10 P5a11b: the save paths of the carried pair (review C2). A reload always docks the pilot at a station (the
// Career keeps no space pose), so the state "the S rides on its L's pad" (`Career.carriedBy`, `parkedL` cleared)
// can't come back as it was: it is released cleanly instead, as a launch would. The L stays parked where it is
// (`parkedL` = its live pose), the cradle is empty and `carriedBy` goes; the pilot reloads in the S at the station
// and flies back to recover it, as after the S's death. The worker needs nothing: it marks the parked L
// `CARRIED_UNTIL` in both states (D-1073, D-1097).
// ---------------------------------------------------------------------------------------------------------------

/** The live parked L's pose to save (`parkedLRecord`), or null when none is spawned or it died. Event-rate. */
export function liveParkedLRecord(): ParkedL | null {
  const b = liveBody
  if (b === null || Life.alive[b.eid] !== 1) return null
  return parkedLRecord(b)
}

/**
 * `career` with the carried state released (C2; pure, the input is never changed): no `carriedBy`, the L's
 * cradle empty, the L parked at `pose` (its live pose). Unchanged when nothing is carried. With no pose for the L
 * (a save from a test build before this card; nothing else can write one) the L is left docked at the pilot's
 * `lastDockedStation`, so the Career never holds a ship that is nowhere.
 */
export function settleCarried(career: Career, pose: Readonly<ParkedL> | null): Career {
  const lId = career.carriedBy
  if (lId === undefined) return career
  const posed = pose !== null && pose.fleetId === lId
  const next: Career = { ...career }
  delete next.carriedBy
  if (career.hangar !== undefined) {
    next.hangar = career.hangar.map((e) => {
      if (e.fleetId !== lId) return e
      const out = { ...e }
      if (e.cradle !== undefined) out.cradle = { carried: null }
      if (!posed) out.stationId = career.lastDockedStation
      return out
    })
  }
  if (posed) next.parkedL = { fleetId: pose.fleetId, systemId: pose.systemId, pos: [pose.pos[0], pose.pos[1], pose.pos[2]], rot: [pose.rot[0], pose.rot[1], pose.rot[2], pose.rot[3]], order: pose.order }
  return next
}
